import { useMemo } from 'react'
import { Image } from '@/atoms/Image'
import { NamedColor } from '@/constants/types'
import { TheatricalReleaseObject } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { CRITIC_IMAGES } from '../ShowtimesView/components/ShowtimeDetailsPanel/ShowtimeDetailsPanel'
import { GuildScoreToolTip } from '../ShowtimesView/components/ToolTips/GuildScoreToolTip'
import { getHoursMinutes } from '../utils'

interface UseTheatricalMetadataProps {
  rating?: string | null
  movieDuration?: number | null
  projectGuildScore?: number | null
  critics?: TheatricalReleaseObject['critics']
  iconOptions?: {
    guildIconColor?: NamedColor
  }
}

interface MetadataItem {
  id: string
  icon?: React.ReactNode
  content: React.ReactNode
}

export const useTheatricalMetadata = ({
  rating,
  movieDuration,
  projectGuildScore,
  critics,
  iconOptions,
}: UseTheatricalMetadataProps): MetadataItem[] => {
  const { locale } = useLocale()

  const rottenTomatoesCritic = critics?.find((critic) => critic?.company === 'Rotten Tomatoes')
  const audienceScore = rottenTomatoesCritic?.scores?.find((score) => score?.name === 'Audience')?.value ?? 0
  const tomatoScore = rottenTomatoesCritic?.scores?.find((score) => score?.name === 'Tomatometer')?.value ?? 0
  const isVerifiedHot = rottenTomatoesCritic?.certifiedFresh ?? false

  const metadataItems = useMemo(() => {
    return [
      {
        id: '0',
        content: rating ?? null,
      },
      {
        id: '1',
        content: movieDuration ? getHoursMinutes(movieDuration / 60, locale) : null,
      },
      {
        id: '2',
        content: projectGuildScore ? (
          <GuildScoreToolTip
            iconSize={16}
            iconColor={iconOptions?.guildIconColor ?? 'core-gray-700'}
            buttonClassName="!p-0 !border-0"
            panelClassName="!z-50"
            guildScore={projectGuildScore}
          />
        ) : null,
      },
      {
        id: '3',
        icon: <Image className="rounded-xl" alt="" src={CRITIC_IMAGES['TOMATO']} width={16} height={16} />,
        content: tomatoScore ? `${tomatoScore}%` : null,
      },
      {
        id: '4',
        icon: (
          <Image
            className="rounded-xl"
            alt=""
            src={isVerifiedHot ? CRITIC_IMAGES['VERIFIED_HOT'] : CRITIC_IMAGES['POPCORN']}
            width={16}
            height={16}
          />
        ),
        content: audienceScore ? `${audienceScore}%` : null,
      },
    ].filter(({ content }) => !!content) as MetadataItem[]
  }, [
    audienceScore,
    isVerifiedHot,
    locale,
    movieDuration,
    projectGuildScore,
    rating,
    tomatoScore,
    iconOptions?.guildIconColor,
  ])

  return metadataItems
}
